import { Activity, Database, RefreshCw, Shield, Swords, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import { StatusPill } from '../components/ui/StatusPill';
import { buildRaidSummary } from '../core/raidSummary';
import { sampleCharacters, sampleRaids } from '../core/sampleData';
import type { CharacterProfile, RaidProgress } from '../core/types';

type ViewKey = 'overview' | 'characters' | 'lockouts' | 'diagnostics';

const views: Array<{ key: ViewKey; label: string; icon: React.ReactNode }> = [
  { key: 'overview', label: 'Сводка', icon: <Activity size={18} /> },
  { key: 'characters', label: 'Персонажи', icon: <Users size={18} /> },
  { key: 'lockouts', label: 'КД', icon: <Swords size={18} /> },
  { key: 'diagnostics', label: 'Диагностика', icon: <Database size={18} /> }
];

export function App() {
  const [activeView, setActiveView] = useState<ViewKey>('overview');
  const summary = useMemo(() => buildRaidSummary(sampleCharacters, sampleRaids), []);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">TW</div>
          <div>
            <div className="brand-title">Twilele WoW</div>
            <div className="brand-subtitle">Raid operations</div>
          </div>
        </div>

        <nav className="nav-list" aria-label="Main navigation">
          {views.map((view) => (
            <button
              key={view.key}
              className={activeView === view.key ? 'nav-item nav-item-active' : 'nav-item'}
              type="button"
              onClick={() => setActiveView(view.key)}
            >
              {view.icon}
              <span>{view.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <h1>{views.find((view) => view.key === activeView)?.label}</h1>
            <p>Локальный прототип на mock data. Следующий шаг - D1 и реальные обновления.</p>
          </div>
          <div className="topbar-actions">
            <Button icon={<RefreshCw size={16} />} variant="primary">Обновить</Button>
          </div>
        </header>

        {activeView === 'overview' ? <Overview summary={summary} /> : null}
        {activeView === 'characters' ? <CharactersTable characters={sampleCharacters} /> : null}
        {activeView === 'lockouts' ? <LockoutTables summary={summary} /> : null}
        {activeView === 'diagnostics' ? <Diagnostics diagnostics={summary.diagnostics} /> : null}
      </main>
    </div>
  );
}

function Overview({ summary }: { summary: ReturnType<typeof buildRaidSummary> }) {
  const total = summary.overall.withoutLockout + summary.overall.partialLockout + summary.overall.fullLockout;

  return (
    <section className="content-flow">
      <div className="metric-grid">
        <Metric label="Без КД" value={summary.overall.withoutLockout} tone="open" />
        <Metric label="Частично с КД" value={summary.overall.partialLockout} tone="partial" />
        <Metric label="С КД" value={summary.overall.fullLockout} tone="closed" />
        <Metric label="Всего учтено" value={total} tone="neutral" />
      </div>

      <div className="split-layout">
        <SummaryTable
          title="Игроки: heroic"
          columns={['Игрок', 'Без КД', 'Част. КД']}
          rows={summary.heroicPlayers.map((row) => [row.player, row.withoutLockout, row.partialLockout])}
        />
        <SummaryTable
          title="Игроки: mythic"
          columns={['Игрок', 'Без КД']}
          rows={summary.mythicPlayers.map((row) => [row.player, row.withoutLockout])}
        />
      </div>
    </section>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone: 'open' | 'partial' | 'closed' | 'neutral' }) {
  return (
    <section className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      <StatusPill label={toneLabel(tone)} tone={tone} />
    </section>
  );
}

function CharactersTable({ characters }: { characters: CharacterProfile[] }) {
  return (
    <section className="table-section">
      <div className="section-heading">
        <h2>Roster</h2>
        <span>{characters.length} characters</span>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Игрок</th>
              <th>Персонаж</th>
              <th>Realm</th>
              <th>Class</th>
              <th>Role</th>
              <th>iLvl</th>
              <th>Heroic</th>
              <th>Mythic</th>
            </tr>
          </thead>
          <tbody>
            {characters.map((character) => (
              <tr key={character.id}>
                <td>{character.player}</td>
                <td className="strong-cell">{character.name}</td>
                <td>{character.realm}</td>
                <td>{character.className}</td>
                <td>{character.role}</td>
                <td>{character.itemLevel}</td>
                <td>{formatCompactLockouts(character.heroicLockouts)}</td>
                <td>{formatCompactLockouts(character.mythicLockouts)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LockoutTables({ summary }: { summary: ReturnType<typeof buildRaidSummary> }) {
  return (
    <section className="split-layout">
      <SummaryTable
        title="Heroic lockouts"
        columns={['Игрок', 'Без КД', 'Част. КД']}
        rows={summary.heroicPlayers.map((row) => [row.player, row.withoutLockout, row.partialLockout])}
      />
      <SummaryTable
        title="Mythic lockouts"
        columns={['Игрок', 'Без КД']}
        rows={summary.mythicPlayers.map((row) => [row.player, row.withoutLockout])}
      />
    </section>
  );
}

function Diagnostics({ diagnostics }: { diagnostics: string[] }) {
  return (
    <section className="table-section">
      <div className="section-heading">
        <h2>Diagnostics</h2>
        <StatusPill label={diagnostics.length ? `${diagnostics.length} issues` : 'OK'} tone={diagnostics.length ? 'partial' : 'open'} />
      </div>
      {diagnostics.length ? (
        <ul className="diagnostic-list">
          {diagnostics.map((entry) => (
            <li key={entry}>{entry}</li>
          ))}
        </ul>
      ) : (
        <div className="empty-state">
          <Shield size={22} />
          <span>Проблем не найдено.</span>
        </div>
      )}
    </section>
  );
}

function SummaryTable({ title, columns, rows }: { title: string; columns: string[]; rows: Array<Array<string | number>> }) {
  return (
    <section className="table-section">
      <div className="section-heading">
        <h2>{title}</h2>
        <span>{rows.length} players</span>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.join('|')}>
                {row.map((cell, index) => (
                  <td key={`${row[0]}-${index}`} className={index === 0 ? 'strong-cell' : undefined}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function formatCompactLockouts(progress: RaidProgress[]) {
  return progress.map((raid) => `${raid.killed}/${raid.total}`).join(' · ');
}

function toneLabel(tone: 'open' | 'partial' | 'closed' | 'neutral') {
  if (tone === 'open') {
    return 'open';
  }

  if (tone === 'partial') {
    return 'partial';
  }

  if (tone === 'closed') {
    return 'locked';
  }

  return 'tracked';
}

